import deepEqual from 'deep-equal'
import Fuse from 'fuse.js'
import { array, bool, func, object, oneOf, string, oneOfType } from 'prop-types'
import { Component } from 'react'
import { WithOutsideTracking } from '../hocs'
import { preventDefault } from '../utils'
import FilterBrowserView from './FilterBrowserView'
import { flatten, findCurrentTree } from './utils'

import './styles.scss'

const isUpOrDownKey = (event) =>
  event.keyCode && (event.keyCode === 38 || event.keyCode === 40)

const onKeyDown = (event) => {
  if (isUpOrDownKey(event)) {
    preventDefault(event)
  }
}

export default class RawFilterBar extends Component {
  static propTypes = {
    currentPath: array,
    disabled: bool,
    disabledValues: array,
    filterTree: array.isRequired,
    filterValues: object,
    focused: bool,
    lastPath: array,
    loading: bool,
    onBlur: func,
    onClearFilters: func,
    onClose: func,
    onDisableAll: func,
    onDisableFilter: func,
    onFilterRemove: func,
    onFilterSelect: func,
    onFilterValueChange: func,
    onFocus: func,
    onPathChange: func,
    onSearch: func,
    searchBarRef: oneOfType([func, object]),
    searchSettings: object,
    searchTerm: string,
    strictSearchFocus: bool,
    theme: oneOf(['dark', 'light', 'timelines-filter']),
    track: func,
    value: array,
  }

  static defaultProps = {
    searchSettings: {
      keys: ['title'],
      threshold: 0.3,
    },
    theme: 'light',
  }

  constructor(props) {
    super(props)

    this.loadProps(props)

    this.state = {
      focused: this.focused || props.focused,
      highlightedFilterId: '',
      shouldScrollToHighlighted: false,
    }

    this.blur = this.blur.bind(this)
    this.focus = this.focus.bind(this)
    this.onFilterMouseMove = this.onFilterMouseMove.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
  }

  componentDidMount() {
    this.setHighlighted(0)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.loadProps(nextProps)
  }

  onFocus() {
    if (!this.props.value.length && !this.props.strictSearchFocus) {
      this.focus()
    }
  }

  focus() {
    if (!this.state.focused) {
      this.setHighlighted(Math.max(this.previousIndex || 0, 0))
    }

    this.setState(() => ({
      focused: true,
    }))
    if (this.props.onFocus) {
      this.props.onFocus()
    }
  }

  blur() {
    if (this.state.focused) {
      this.previousIndex = this.index || 0
    }

    this.setHighlighted(-1)
    this.setState(() => ({
      focused: false,
    }))
    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  onKeyUp(event) {
    if (event && event.keyCode) {
      switch (event.keyCode) {
        case 38: // up key
          event.preventDefault()
          this.decrementHighlighted()
          break
        case 40: // down key
          event.preventDefault()
          this.incrementHighlighted()
          break
        case 37: // left key
          this.traverseBack()
          break
        case 39: // right key
          this.traverseForward()
          break
        case 13: // enter key
          this.onEnterKey(event)
          break
        default:
          break
      }
    }
  }

  onEnterKey(event) {
    const filter = this.highlightedFilter
    if (filter.id === this.currentPathTail) {
      this.traverseBack()
    } else if (filter.childOptions && filter.childOptions.length) {
      this.traverseForward()
    } else {
      const isSelected = this.props.value.includes(filter.id)
      if (isSelected) {
        this.props.onFilterRemove(filter.id)
      } else {
        this.props.onFilterSelect({ id: filter.id, disabled: false })
      }

      if (this.props.searchTerm) {
        event.target.select()
      }
    }
  }

  traverseBack() {
    const path = this.props.currentPath || []
    if (path && path.length) {
      this.props.onPathChange(path.slice(0, -1))
    }
  }

  traverseForward() {
    const filter = this.highlightedFilter
    const hasValidChildren =
      filter && filter.childOptions && filter.childOptions.length
    if (hasValidChildren && filter.id !== this.currentPathTail) {
      this.props.onPathChange([...(this.props.currentPath || []), filter.id])
    }
  }

  loadProps(props) {
    const { currentPath, filterTree, searchTerm, value, disabledValues } = props

    if (!this.fuse || filterTree !== this.props.filterTree) {
      this.flatTree = filterTree.flatTree || flatten(filterTree)
      this.fuse = new Fuse(this.flatTree, this.props.searchSettings)
    }

    if (searchTerm) {
      if (searchTerm !== this.props.searchTerm) {
        this.filteredResults = this.fuse
          .search(searchTerm)
          .map((result) => result.item)
          .filter((f) => !f.childOptions || !f.childOptions.length)
        this.currentFilterTree = this.filteredResults
        this.setHighlighted(-1, this.incrementHighlighted)
      }
    } else {
      this.currentFilterTree = findCurrentTree(
        props.currentPath,
        props.filterTree
      )
      if (this.props.searchTerm) {
        this.setHighlighted(0)
      }
    }

    if (!deepEqual(currentPath, this.props.currentPath)) {
      this.setHighlighted(this.getPathIndex(currentPath || []))
    }

    if (
      !value.length &&
      this.props.value.length &&
      !this.props.strictSearchFocus
    ) {
      this.focus()
    }

    this.enabledFilters = value.filter(
      (filterId) => disabledValues.indexOf(filterId) < 0
    )
  }

  setHighlighted(idx, cb, scrollToHighlighted = false) {
    this.index = idx
    const result = this.highlightedFilter
    const highlightedFilterId = result && result.id

    if (
      this.state.highlightedFilterId !== highlightedFilterId ||
      this.state.shouldScrollToHighlighted !== scrollToHighlighted
    ) {
      this.setState(
        {
          highlightedFilterId: highlightedFilterId || '',
          shouldScrollToHighlighted: scrollToHighlighted,
        },
        () => {
          if (cb) {
            cb.call(this)
          }
        }
      )
    }
  }

  incrementHighlighted() {
    const len = this.currentFilterTree ? this.currentFilterTree.length - 1 : 0
    this.setHighlighted(Math.min(this.highlightIndex + 1, len), null, true)
  }

  decrementHighlighted() {
    this.setHighlighted(Math.max(0, this.highlightIndex - 1), null, true)
  }

  getPathIndex(newPath) {
    const oldPath = this.props.currentPath || []
    if (newPath.length > oldPath.length) {
      return 0
    }

    const tail = this.currentPathTail
    return Math.max(
      0,
      this.currentFilterTree.findIndex((f) => f.id === tail)
    )
  }

  onFilterMouseMove(filterId) {
    const filterIndex = this.currentFilterTree.findIndex(
      (f) => f.id === filterId
    )
    this.setHighlighted(filterIndex)
  }

  get focused() {
    return Boolean((this.state || {}).focused || this.props.value.length === 0)
  }

  get highlightIndex() {
    if (this.index === undefined || Number.isNaN(this.index)) {
      this.index = -1
    }
    return this.index
  }

  get scrollToIndex() {
    const shouldScroll = this.state.shouldScrollToHighlighted
    if (
      shouldScroll &&
      this.currentFilterTree &&
      this.currentFilterTree.length
    ) {
      return this.highlightIndex
    }
    return null
  }

  get highlightedFilter() {
    return this.currentFilterTree[this.highlightIndex]
  }

  get currentPathTail() {
    const path = this.props.currentPath || []
    return path.length ? path.slice(-1)[0] : undefined
  }

  render() {
    const {
      currentPath = [],
      disabled,
      disabledValues,
      filterTree,
      filterValues,
      lastPath,
      loading = false,
      onClearFilters,
      onClose,
      onDisableAll,
      onDisableFilter,
      onFilterRemove,
      onFilterSelect,
      onFilterValueChange,
      onPathChange,
      onSearch,
      searchBarRef,
      searchTerm = '',
      theme,
      track,
      value,
    } = this.props
    const { highlightedFilterId } = this.state
    const { focused, enabledFilters, onFilterMouseMove } = this

    return (
      <WithOutsideTracking onOutsideClick={this.blur}>
        <FilterBrowserView
          currentPath={currentPath}
          disabled={disabled}
          disabledValues={disabledValues}
          enabledFilters={enabledFilters}
          filters={this.flatTree}
          filterTree={filterTree}
          filterValues={filterValues}
          focused={focused}
          highlightedFilterId={highlightedFilterId}
          lastPath={lastPath}
          loading={loading}
          onClearFilters={onClearFilters}
          onClose={onClose}
          onDisableAll={onDisableAll}
          onDisableFilter={onDisableFilter}
          onFilterMouseMove={onFilterMouseMove}
          onFilterRemove={onFilterRemove}
          onFilterSelect={onFilterSelect}
          onFilterValueChange={onFilterValueChange}
          onFocus={this.onFocus}
          onKeyDown={onKeyDown}
          onKeyUp={this.onKeyUp}
          onPathChange={onPathChange}
          onSearch={onSearch}
          onSearchBarFocus={this.focus}
          results={this.filteredResults}
          scrollToIndex={this.scrollToIndex}
          searchBarRef={searchBarRef}
          searchTerm={searchTerm}
          theme={theme}
          track={track}
          value={value}
        />
      </WithOutsideTracking>
    )
  }
}
